import { ISO8601DateString } from '@portal-core/data/common/types/ISO-date-string.type';
import { StatusIncidentType } from '@portal-core/status-incidents/enums/status-incident-type.enum';
import { StatusIncidentStatus } from '@portal-core/status-incidents/enums/status-incident-status.enum';
import { StatusIncidentUpdate } from '@portal-core/status-incidents/models/status-incident-update.model';
import { StatusIncident } from '@portal-core/status-incidents/models/status-incident.model';

/** Statuses that mean an incident is over, across both lifecycles. */
const TerminalStatuses: ReadonlySet<StatusIncidentStatus> = new Set([
  StatusIncidentStatus.Resolved,
  StatusIncidentStatus.Postmortem,
  StatusIncidentStatus.Completed
]);

/** Each type's lifecycle, in order. Mirrors StatusIncidentStatusExtensions.IsValidFor on the server. */
const LifecycleStatuses: { [key in StatusIncidentType]: readonly StatusIncidentStatus[] } = {
  [StatusIncidentType.Unplanned]: [
    StatusIncidentStatus.Investigating,
    StatusIncidentStatus.Identified,
    StatusIncidentStatus.Monitoring,
    StatusIncidentStatus.Resolved,
    StatusIncidentStatus.Postmortem
  ],
  [StatusIncidentType.ScheduledMaintenance]: [
    StatusIncidentStatus.Scheduled,
    StatusIncidentStatus.InProgress,
    StatusIncidentStatus.Verifying,
    StatusIncidentStatus.Completed
  ]
};

/** How a type is written for an administrator. The public page words these itself. */
const TypeLabels: { [key in StatusIncidentType]: string } = {
  [StatusIncidentType.Unplanned]: 'Unplanned',
  [StatusIncidentType.ScheduledMaintenance]: 'Scheduled Maintenance'
};

/**
 * The visibility choice offered when creating an incident, in the order it is offered.
 *
 * Publication is a separate axis from StatusIncidentStatus: a draft still carries a real lifecycle
 * status, it simply is not customer-visible yet.
 */
export const SelectableIncidentVisibilities: readonly { published: boolean, label: string }[] = [
  { published: false, label: 'Draft' },
  { published: true, label: 'Published' }
];

/**
 * Types in the order they are offered for selection, which is deliberate rather than the enum's
 * ordinal order: scheduled maintenance is both the more common case and the form default.
 */
export const SelectableIncidentTypes: readonly StatusIncidentType[] = [
  StatusIncidentType.ScheduledMaintenance,
  StatusIncidentType.Unplanned
];

export function typeLabel(type: StatusIncidentType): string {
  return TypeLabels[type] ?? '';
}

/** The statuses an incident of this type can have, in lifecycle order. */
export function statusesFor(type: StatusIncidentType): StatusIncidentStatus[] {
  return [...(LifecycleStatuses[type] ?? [])];
}

/** The status an incident of this type opens with. */
export function initialStatusFor(type: StatusIncidentType): StatusIncidentStatus {
  return type === StatusIncidentType.ScheduledMaintenance ? StatusIncidentStatus.Scheduled : StatusIncidentStatus.Investigating;
}

/** The status an ordinary progress update carries for this type. */
export function progressStatusFor(type: StatusIncidentType): StatusIncidentStatus {
  return type === StatusIncidentType.ScheduledMaintenance ? StatusIncidentStatus.InProgress : StatusIncidentStatus.Investigating;
}

/** The status that closes an incident of this type. */
export function closingStatusFor(type: StatusIncidentType): StatusIncidentStatus {
  return type === StatusIncidentType.ScheduledMaintenance ? StatusIncidentStatus.Completed : StatusIncidentStatus.Resolved;
}

/**
 * Whether this is the update an incident opened with, which cannot be deleted or re-statused.
 *
 * Decided by position rather than status. Scheduled maintenance opens with Scheduled, which is unique, but
 * an unplanned incident opens with Investigating, which it can return to later - so no status identifies it.
 */
export function isFirstUpdate(incident: StatusIncident, update: StatusIncidentUpdate): boolean {
  const live = (incident?.Updates ?? []).filter(u => !u.IsDeleted);
  if (!update || live.length === 0) {
    return false;
  }

  return update.Id === live.reduce((earliest, u) => (u.Id < earliest ? u.Id : earliest), live[0].Id);
}

export function isTerminalStatus(status: StatusIncidentStatus): boolean {
  return TerminalStatuses.has(status);
}

export function isIncidentActive(incident: StatusIncident): boolean {
  return !!incident && !isTerminalStatus(incident.Status);
}

export function isScheduled(incident: StatusIncident): boolean {
  return incident?.Type === StatusIncidentType.ScheduledMaintenance;
}

/**
 * When an incident began, or is due to.
 *
 * Scheduled maintenance is announced before it starts, so it is described by when it was scheduled for until
 * it actually begins. An unplanned incident only has the moment it started.
 */
export function incidentStartAt(incident: StatusIncident): ISO8601DateString {
  if (!incident) {
    return undefined;
  }

  return isScheduled(incident)
    ? incident.StartedAt ?? incident.ScheduledFor
    : incident.StartedAt ?? incident.PublishedAt ?? incident.CreatedOn;
}

/** When an incident ended, or is due to. Null while it is still open with no scheduled end. */
export function incidentEndAt(incident: StatusIncident): ISO8601DateString {
  if (!incident) {
    return undefined;
  }

  return incident.ResolvedAt ?? (isScheduled(incident) ? incident.ScheduledUntil : undefined);
}